import { isValidEmail } from "src/utils/Email";
import {
  CUSTOM_OPTION,
  EMAIL_WIZARD_STEPS,
  SUB_CAMPAIGN_OPTIONS,
} from "./AiCampaignConstants";
import type {
  AiCampaignForm,
  AiCampaignStepKey,
  CampaignTypeSelection,
  ChoiceOption,
  EmailDesign,
} from "./AiCampaignTypes";

const ERROR_KEY = "campaignFlow.aiWizard.errors";

export const formatCategoryName = (category: string): string =>
  (category.split("/").pop() || category)
    .replace(/-/g, " ")
    .replace(/\b\w/g, char => char.toUpperCase());

export const isCustomCampaignType = (form: AiCampaignForm): boolean =>
  form.campaignType === CUSTOM_OPTION;

export const resolveCampaignType = (form: AiCampaignForm): string =>
  isCustomCampaignType(form) ? form.customCampaignType.trim() : form.campaignType ?? "";

export const resolveSubCampaign = (form: AiCampaignForm): string =>
  isCustomCampaignType(form) || form.subCampaign === CUSTOM_OPTION
    ? form.customSubCampaign.trim()
    : form.subCampaign ?? "";

export const getSubCampaignOptions = (
  campaignType: CampaignTypeSelection | null
): ChoiceOption[] =>
  campaignType && campaignType !== CUSTOM_OPTION ? SUB_CAMPAIGN_OPTIONS[campaignType] : [];

const isBlank = (value: string) => !value.trim();

const getCategoryError = (form: AiCampaignForm): string | null => {
  if (!form.campaignType) {
    return `${ERROR_KEY}.selectCampaignCategory`;
  }
  if (isCustomCampaignType(form) && isBlank(form.customCampaignType)) {
    return `${ERROR_KEY}.enterCategoryName`;
  }
  if (isCustomCampaignType(form) && isBlank(form.customSubCampaign)) {
    return `${ERROR_KEY}.enterSubCategoryName`;
  }
  return null;
};

const getSubCategoryError = (form: AiCampaignForm): string | null => {
  if (isCustomCampaignType(form)) {
    return null;
  }
  if (!form.subCampaign) {
    return `${ERROR_KEY}.selectSubCategory`;
  }
  if (form.subCampaign === CUSTOM_OPTION && isBlank(form.customSubCampaign)) {
    return `${ERROR_KEY}.enterSubCategoryName`;
  }
  return null;
};

const getPromptError = (form: AiCampaignForm): string | null => {
  const requiredFields: [string, string][] = [
    [form.prompt, "enterPrompt"],
    [form.tone ?? "", "selectTone"],
    [form.persona, "enterPersona"],
    [form.seasonalTag, "enterSeasonalTag"],
    [form.ctaUrl, "enterCtaUrl"],
    [form.ctaLabel, "enterCtaLabel"],
  ];
  const missingField = requiredFields.find(([value]) => isBlank(value));
  return missingField ? `${ERROR_KEY}.${missingField[1]}` : null;
};

export const MAX_REPLY_TO_LENGTH = 254;

export interface StepValidationContext {
  sendTo: string[];
  replyTo: string;
}

export const getCampaignDetailsError = (campaignName: string, sendTo: string[]): string | null => {
  if (isBlank(campaignName)) {
    return `${ERROR_KEY}.enterCampaignName`;
  }
  return sendTo.length ? null : `${ERROR_KEY}.selectSendTo`;
};

export const getSenderRecipientsError = (
  subjectLine: string,
  { sendTo, replyTo }: StepValidationContext
): string | null => {
  if (isBlank(subjectLine)) {
    return `${ERROR_KEY}.enterSubjectLine`;
  }
  const trimmedReplyTo = replyTo.trim();
  if (trimmedReplyTo && (trimmedReplyTo.length > MAX_REPLY_TO_LENGTH || !isValidEmail(trimmedReplyTo))) {
    return `${ERROR_KEY}.enterReplyTo`;
  }
  return sendTo.length ? null : `${ERROR_KEY}.selectSendTo`;
};

const REVIEWED_STEP_KEYS: AiCampaignStepKey[] = [
  "campaignDetails",
  "productCategories",
  "campaignCategory",
  "campaignSubCategory",
  "aiPrompt",
  "senderRecipients",
];

export const getEmailStepValidationError = (
  stepKey: AiCampaignStepKey,
  form: AiCampaignForm,
  context: StepValidationContext
): string | null => {
  switch (stepKey) {
    case "campaignDetails":
      return getCampaignDetailsError(form.campaignName, context.sendTo);
    case "productCategories":
      return form.selectedCategories.length ? null : `${ERROR_KEY}.selectCategory`;
    case "campaignCategory":
      return getCategoryError(form);
    case "campaignSubCategory":
      return getSubCategoryError(form);
    case "aiPrompt":
      return getPromptError(form);
    case "senderRecipients":
      return getSenderRecipientsError(form.subjectLine, context);
    case "reviewDetails":
      return (
        REVIEWED_STEP_KEYS.map(key => getEmailStepValidationError(key, form, context)).find(Boolean) ??
        null
      );
    default:
      return null;
  }
};

const EMAIL_SUB_CATEGORY_STEP_INDEX = EMAIL_WIZARD_STEPS.findIndex(
  step => step.key === "campaignSubCategory"
);

const shouldSkipEmailStep = (index: number, form: AiCampaignForm) =>
  index === EMAIL_SUB_CATEGORY_STEP_INDEX && isCustomCampaignType(form);

export const getEmailAdjacentStepIndex = (
  currentIndex: number,
  direction: 1 | -1,
  form: AiCampaignForm
): number => {
  let nextIndex = currentIndex + direction;
  while (shouldSkipEmailStep(nextIndex, form)) {
    nextIndex += direction;
  }
  return nextIndex;
};

export const parseGeneratedDesign = (design: unknown): EmailDesign | null => {
  if (!design) {
    return null;
  }
  if (typeof design !== "string") {
    return design as EmailDesign;
  }
  try {
    return JSON.parse(design) as EmailDesign;
  } catch {
    return null;
  }
};
